import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "fileInput",
    "previewTable",
    "submitButton",
    "previewContainer",
    "fileName",
    "queryCount",
  ];

  connect() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
    }
  }

  handleFileSelect() {
    const fileInput = this.fileInputTarget;
    const fileNameElement = this.fileNameTarget;
    const previewContainer = this.previewContainerTarget;
    const previewTable = this.previewTableTarget;
    const queryCountElement = this.queryCountTarget;

    if (!fileInput.files || fileInput.files.length === 0) {
      fileNameElement.textContent = "No file chosen";
      previewContainer.classList.add("hidden");
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = true;
      }
      return;
    }

    const file = fileInput.files[0];

    fileNameElement.textContent = file.name;
    fileNameElement.classList.add("font-medium", "text-indigo-600");

    if (!file.name.endsWith(".csv")) {
      this.showError(
        "Please select a valid CSV file (must end with .csv extension)"
      );
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target.result;

        let rows = this.parseCSV(content);
        if (!rows || rows.length === 0) {
          throw new Error("CSV appears to be empty");
        }

        const headers = rows[0].map((header) => header.trim().toLowerCase());

        if (!headers.includes("query_text")) {
          this.showError("CSV must contain a 'query_text' column");
          return;
        }

        const queryTextIndex = headers.indexOf("query_text");

        let validQueries = [];

        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (row.length > queryTextIndex) {
            const queryText = row[queryTextIndex].trim();
            if (queryText !== "") {
              validQueries.push(queryText);
            }
          }
        }

        queryCountElement.textContent = validQueries.length;

        let tableHTML = "";

        const previewQueries = validQueries.slice(0, 5);
        previewQueries.forEach((queryText) => {
          tableHTML += `
            <tr>
              <td class="px-6 py-3 text-left whitespace-nowrap text-sm text-gray-900">${this.escapeHTML(
                queryText
              )}</td>
            </tr>
          `;
        });

        if (validQueries.length > 5) {
          tableHTML += `
            <tr>
              <td class="px-6 py-3 text-sm text-gray-500 text-left italic">
                ... and ${validQueries.length - 5} more queries
              </td>
            </tr>
          `;
        }

        previewTable.innerHTML = tableHTML;
        previewContainer.classList.remove("hidden");

        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = validQueries.length === 0;
        }
      } catch (error) {
        this.showError(`Error parsing CSV: ${error.message}`);
      }
    };

    reader.onerror = () => {
      this.showError("Error reading the file");
    };

    reader.readAsText(file);
  }

  showError(message) {
    this.previewTableTarget.innerHTML = `
      <tr>
        <td class="px-6 py-3 text-sm text-red-600">
          ${message}
        </td>
      </tr>
    `;
    this.previewContainerTarget.classList.remove("hidden");

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
    }

    this.fileNameTarget.classList.remove("font-medium", "text-indigo-600");
    this.queryCountTarget.textContent = "0";
  }

  parseCSV(text) {
    const result = [];
    let row = [];
    let field = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        row.push(field);
        field = "";
      } else if (
        (char === "\n" || (char === "\r" && nextChar === "\n")) &&
        !inQuotes
      ) {
        row.push(field);
        result.push(row);
        row = [];
        field = "";

        if (char === "\r") {
          i++;
        }
      } else {
        field += char;
      }
    }

    if (field !== "" || row.length > 0) {
      row.push(field);
      result.push(row);
    }

    return result;
  }

  escapeHTML(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
}
